
import { useState } from "react";
import { PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfigurationManager } from "@/components/ConfigurationManager";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { ScrollArea } from "@/components/ui/scroll-area";

type Configuration = {
  id: string;
  name: string;
  description: string | null;
  script_content: string | null;
  script_type: string;
  user_id: string | null;
  created_at: string;
  updated_at: string;
  devices_count?: number;
};

const ScriptsPage = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedScript, setSelectedScript] = useState<Configuration | null>(null);
  const queryClient = useQueryClient();

  const { data: scripts, isLoading, error } = useQuery({
    queryKey: ["scripts"],
    queryFn: async () => {
      console.log("Fetching scripts...");
      const { data: scriptsData, error: scriptsError } = await supabase
        .from("configurations")
        .select(`
          *
        `)
        .order("created_at", { ascending: false });

      if (scriptsError) {
        console.error("Error fetching scripts:", scriptsError);
        throw scriptsError;
      }
      
      console.log("Fetched scripts:", scriptsData);
      return scriptsData as Configuration[];
    },
  });

  const handleCreateSuccess = () => {
    setIsDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: ["scripts"] });
  };

  const handleScriptClick = (script: Configuration) => {
    setSelectedScript(script);
  };

  if (error) {
    console.error("Error in scripts query:", error);
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 p-6 w-full">
      <div className="w-full min-w-0 mx-auto">
        <div className="flex justify-between items-start mb-8">
          <div className="text-left">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              Scripts
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              Manage and monitor your device configuration scripts
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-black text-white hover:bg-black/90" size="lg">
                <PlusCircle className="mr-2 h-5 w-5" />
                Create New Script
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <ConfigurationManager onSuccess={handleCreateSuccess} />
            </DialogContent>
          </Dialog>
        </div>

        <div className="w-full overflow-auto rounded-md border bg-white dark:bg-slate-800">
          <Table style={{ minWidth: '100%' }}>
            <TableHeader>
              <TableRow>
                <TableHead className="text-left">Name</TableHead>
                <TableHead className="text-left">Description</TableHead>
                <TableHead className="text-left">Type</TableHead>
                <TableHead className="text-left">Created</TableHead>
                <TableHead className="text-left">Last Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-4">
                    Loading scripts...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-4 text-red-500">
                    Error loading scripts. Please try again.
                  </TableCell>
                </TableRow>
              ) : scripts?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-4">
                    No scripts found. Create your first script to get started.
                  </TableCell>
                </TableRow>
              ) : (
                scripts?.map((script) => (
                  <TableRow 
                    key={script.id} 
                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700"
                    onClick={() => handleScriptClick(script)}
                  >
                    <TableCell className="font-medium text-left">{script.name}</TableCell>
                    <TableCell className="text-left">{script.description}</TableCell>
                    <TableCell className="text-left">{script.script_type}</TableCell>
                    <TableCell className="text-left">
                      {new Date(script.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-left">
                      {new Date(script.updated_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Script Details Dialog */}
        <Dialog open={!!selectedScript} onOpenChange={(open) => !open && setSelectedScript(null)}>
          <DialogContent className="sm:max-w-[600px]">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">{selectedScript?.name}</h3>
              <div className="space-y-2">
                <p className="text-sm text-slate-500">{selectedScript?.description}</p>
                <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-md">
                  <ScrollArea className="h-[300px]">
                    <pre className="text-sm font-mono whitespace-pre-wrap">
                      {selectedScript?.script_content}
                    </pre>
                  </ScrollArea>
                </div>
                <div className="text-sm text-slate-500">
                  <p>Type: {selectedScript?.script_type}</p>
                  <p>Created: {selectedScript?.created_at && new Date(selectedScript.created_at).toLocaleString()}</p>
                  <p>Last Updated: {selectedScript?.updated_at && new Date(selectedScript.updated_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ScriptsPage;
